import React, { useEffect, useState } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import ApiClient from "../../utils/ApiClient";
import RAPackingListTable from "./RAPackingListTable";
import RAShipmentScan from "./RAShipmentScan";
import PoStats from "./PoStats";

export default function RAPackingList({ poSelected, scannedData, setScannedData }) {
    const [packingLists, setPackingLists] = useState([]);
    const [packingListSelected, setPackingListSelected] = useState("");
    const [packingListData, setPackingListData] = useState([]);
    const [isTableOpen, setIsTableOpen] = useState(true);
    const [packingListName, setPackingListName] = useState("");
    const [loadingPackingLists, setLoadingPackingLists] = useState(false);
    const [loadingPackingListData, setLoadingPackingListData] = useState(false);
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingScanData, setLoadingScanData] = useState(false);
    const [poStats, setPoStats] = useState([]);
    const [sfpByAsin, setSfpByAsin] = useState({});

    // PO SFP split: per-ASIN SFP rack state for this PO; refreshed after every scan and every rack.
    const loadSfpStatus = React.useCallback(async () => {
        if (!poSelected) { setSfpByAsin({}); return; }
        try {
            const res = await ApiClient.get(`/api/local-sfp/rack-status?poNumber=${encodeURIComponent(poSelected)}`);
            const map = {};
            (res.data || []).forEach(l => { map[(l.asin || '').toUpperCase()] = l; });
            setSfpByAsin(map);
        } catch (e) {
            console.error('Failed to load SFP rack status', e);
            setSfpByAsin({});
        }
    }, [poSelected]);

    useEffect(() => { loadSfpStatus(); }, [loadSfpStatus, scannedData]);

    // Clear everything when PO changes
    useEffect(() => {
        setScannedData([]); // Clear scan data first
        setPackingLists([]);
        setPackingListSelected("");
        setPackingListData([]);
        setPoStats([]);

        if (poSelected) {
            setLoadingPackingLists(true);
            ApiClient.post("/api/receiveinventory/packinglistfrompo", { poNumber: poSelected })
                .then((response) => {
                    setPackingLists(response.data || []);
                })
                .catch((error) => {
                    console.error("Failed to fetch packing lists", error);
                    setPackingLists([]);
                })
                .finally(() => {
                    setLoadingPackingLists(false);
                });
        }
    }, [poSelected, setScannedData]);

    // Clear scan data and fetch new data when packing list changes
    useEffect(() => {
        setScannedData([]);

        if (!packingListSelected || !poSelected) {
            setPackingListData([]);
            return;
        }

        const fetchData = async () => {
            try {
                setLoadingPackingListData(true);
                setLoadingStats(true);
                setLoadingScanData(true);
                setPoStats([]);

                // Get packing list data
                const packingListResponse = await ApiClient.post("/api/receiveinventory/getpackinglistdata", {
                    poNumber: poSelected,
                    packingListId: packingListSelected,
                });
                console.log(packingListResponse)
                setPackingListData(packingListResponse.data || []);
                setLoadingPackingListData(false);

                // Get PO stats data
                const poStatsResponse = await ApiClient.post("/api/receiveinventory/packingliststats", {
                    poNumber: poSelected,
                    packingListId: packingListSelected,
                });
                setPoStats(poStatsResponse.data || []);
                setLoadingStats(false);

                // Get scan history
                const scannedHistoryResponse = await ApiClient.post("/api/getscannedpackinglisthistory", {
                    packingListId: packingListSelected,
                    poNumber: poSelected,
                });
                setScannedData(scannedHistoryResponse.data || []);
                setLoadingScanData(false);
            } catch (error) {
                console.error("Failed to fetch data:", error);
                setScannedData([]);
                setLoadingPackingListData(false);
                setLoadingStats(false);
                setLoadingScanData(false);
            }
        };

        fetchData();
    }, [packingListSelected, poSelected, setScannedData]);

    const handlePackingListChange = (e) => {
        setScannedData([]);
        const selectedValue = e.target.value;
        const selectedOption = packingLists.find(
            (option) => option.value === selectedValue
        );
        setPackingListSelected(selectedValue);
        if (selectedOption) {
            setPackingListName(selectedOption.label);
        }
    };

    // Function to update scanned data
    const updateScannedData = async () => {
        if (!packingListSelected || !poSelected) {
            setScannedData([]);
            return;
        }

        try {
            setLoadingScanData(true);
            const response = await ApiClient.post("/api/getscannedpackinglisthistory", {
                packingListId: packingListSelected,
                poNumber: poSelected,
            });
            setScannedData(response.data || []);
        } catch (error) {
            console.error("Failed to update scanned data:", error);
            setScannedData([]);
        } finally {
            setLoadingScanData(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
    );

    return (
        <div>
            <div className="relative flex flex-col w-[400px]">
                <div className="flex-1">
                    <label>Select Packing List To Receive Against</label>
                    <select
                        className="border border-gray-300 rounded px-3 py-2 focus:ring focus:outline-none w-full"
                        value={packingListSelected}
                        onChange={handlePackingListChange}
                        disabled={loadingPackingLists}
                    >
                        {loadingPackingLists ? (
                            <option value="" disabled>Loading Packing Lists...</option>
                        ) : packingLists.length === 0 ? (
                            <option value="" disabled>No Packing Lists Created</option>
                        ) : (
                            <>
                                <option value="" disabled>Select Packing List</option>
                                {packingLists.map((option, index) => (
                                    <option key={index} value={option.value}>
                                        {option.label} - Shipment {option.shipment_id}
                                    </option>
                                ))}
                            </>
                        )}
                    </select>
                </div>
            </div>

            {/* Stats Section*/}
            {loadingStats ? (
                <LoadingSpinner />
            ) : (
                <PoStats poStats={poStats} />
            )}

            {/* Packing List Table Section */}
            {loadingPackingListData ? (
                <div className="mt-4 border-t pt-4">
                    <LoadingSpinner />
                </div>
            ) : packingListData && packingListData.length > 0 && (
                <div className="mt-4 border-t pt-4">
                    <div
                        className="flex justify-between items-center cursor-pointer bg-gray-100 px-4 py-3 rounded-t-lg shadow-sm hover:bg-gray-300 transition"
                        onClick={() => setIsTableOpen(!isTableOpen)}
                    >
                        <h3 className="text-lg font-semibold text-gray-800">{packingListName || "Packing List"}</h3>
                        <button className={`transform transition-transform ${isTableOpen ? "rotate-180" : "rotate-0"}`}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-gray-600"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4 4a.75.75 0 01-1.06 0l-4-4a.75.75 0 01.02-1.06z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>

                    {isTableOpen && (
                        <div className="bg-white px-4 py-4 rounded-b-lg shadow-sm">
                            <RAPackingListTable packingListData={packingListData} sfpByAsin={sfpByAsin} onRacked={loadSfpStatus} />
                        </div>
                    )}
                </div>
            )}

            {/* Shipment Scan Section */}
            <div className="mt-4 border-t pt-4">
                <div className="flex justify-between items-center cursor-pointer bg-gray-100 px-4 py-3 rounded-t-lg shadow-sm hover:bg-gray-300 transition">
                    <h3 className="text-lg font-semibold text-gray-800">Shipment Scan</h3>
                </div>
                <div className="bg-white px-4 py-4 rounded-b-lg shadow-sm">
                    {loadingScanData ? (
                        <LoadingSpinner />
                    ) : (
                        <RAShipmentScan
                            scanData={scannedData}
                            setScannedData={setScannedData}
                            packingListData={packingListData}
                            packingListId={packingListSelected}
                            poNumber={poSelected}
                            setPackingListData={setPackingListData}
                            onScannedDataUpdate={updateScannedData}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}